import React, { useEffect, useMemo, useState } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import SearchIcon from "@material-ui/icons/Search";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CloseIcon from "@material-ui/icons/Close";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";
import { getAdminTexts } from "../../translate/pageTexts";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		alignItems: "flex-start",
		padding: theme.spacing(5, 3, 3),
	},
	container: {
		maxWidth: 980,
	},
	header: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: theme.spacing(2),
	},
	tabsPaper: {
		marginBottom: theme.spacing(2),
		borderRadius: 10,
	},
	paper: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		marginBottom: 12,
	},
	integrationSection: {
		padding: theme.spacing(2),
		marginBottom: theme.spacing(2),
		borderRadius: 10,
	},
	// Um cartao por regra de timeout. Sem essa separacao os quatro conjuntos
	// viravam uma parede unica de campos, e nao dava para saber de relance
	// qual "Fila de destino" pertencia a qual regra.
	ruleCard: {
		padding: theme.spacing(2),
		marginBottom: theme.spacing(2),
		borderRadius: 10,
	},
	ruleTitle: {
		fontWeight: 700,
		marginBottom: theme.spacing(0.5),
	},
	ruleDescription: {
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(1.5),
		display: "block",
	},
	sectionHeader: {
		display: "flex",
		alignItems: "flex-start",
		justifyContent: "space-between",
		gap: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},
	transcriptionGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
		gap: theme.spacing(2),
		[theme.breakpoints.down("sm")]: {
			gridTemplateColumns: "1fr",
		},
	},
	integrationsToolbar: {
		display: "flex",
		gap: theme.spacing(2),
		alignItems: "center",
		marginBottom: theme.spacing(2),
	},
	searchField: {
		flex: 1,
	},
	generateButton: {
		minWidth: 170,
		borderRadius: 10,
		fontWeight: 700,
	},
	tablePaper: {
		borderRadius: 8,
		overflow: "hidden",
	},
	statusChip: {
		height: 24,
		fontWeight: 700,
	},
	dialogPaper: {
		borderRadius: 12,
	},
	tokenRevealBox: {
		padding: theme.spacing(2),
		borderRadius: 8,
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(15, 23, 42, 0.72)"
				: "rgba(241, 245, 249, 0.96)",
		border: `1px solid ${theme.palette.divider}`,
		wordBreak: "break-all",
		fontFamily: "monospace",
	},
	dialogTitle: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},
	emptyState: {
		padding: theme.spacing(4),
		textAlign: "center",
		color: theme.palette.text.secondary,
	},
	menuIcon: {
		marginRight: theme.spacing(1),
		fontSize: 18,
	},
	dangerItem: {
		color: theme.palette.error.main,
	},
	formField: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
}));

const expirationOptions = [
	{ value: "30d", label: "30 dias" },
	{ value: "90d", label: "90 dias" },
	{ value: "180d", label: "180 dias" },
	{ value: "1y", label: "1 ano" },
	{ value: "never", label: "Não expirar" },
];

const timezoneOptions = [
	{ value: "America/Sao_Paulo", label: "Brasília (America/Sao_Paulo)" },
	{ value: "America/Fortaleza", label: "Fortaleza (America/Fortaleza)" },
	{ value: "America/Belem", label: "Belém (America/Belem)" },
	{ value: "America/Cuiaba", label: "Cuiabá (America/Cuiaba)" },
	{ value: "America/Manaus", label: "Manaus (America/Manaus)" },
	{ value: "America/Rio_Branco", label: "Rio Branco (America/Rio_Branco)" },
	{ value: "America/Noronha", label: "Fernando de Noronha (America/Noronha)" },
	{ value: "UTC", label: "UTC" },
];

const transcriptionDefaults = {
	enabled: "disabled",
	provider: "openai",
	apiKey: "",
	model: "whisper-1",
	endpoint: "",
	language: "pt",
};

const ratingDefaults = {
	enabled: "disabled",
	message:
		"Como você avalia este atendimento?\nResponda com uma nota de 1 a 5.",
	thankYouMessage: "Obrigado pela sua avaliação!",
	allowComment: "enabled",
	expiresHours: "24",
};

const messagingDefaults = {
	acceptedEnabled: "disabled",
	acceptedMessage:
		"Olá {{firstname}}, seu atendimento foi aceito por {{user}} e já vamos te ajudar.",
	transferToUserEnabled: "disabled",
	transferToUserMessage:
		"Olá {{firstname}}, seu atendimento foi transferido e agora será atendido por {{user}}.",
	transferToQueueEnabled: "disabled",
	transferToQueueMessage:
		"Olá {{firstname}}, seu atendimento foi transferido para a fila {{queue}}.",
	storeAsNote: "disabled",
};

const timeoutDefaults = {
	noQueueMinutes: "0",
	noQueueAction: "close",
	noQueueQueueId: "",
	noQueueMessageEnabled: "disabled",
	noQueueMessage:
		"Olá {{firstname}}, seu atendimento ficou sem resposta e será encerrado. Se precisar, é só chamar de novo.",
	openMinutes: "0",
	openAction: "close",
	openMessageEnabled: "disabled",
	openMessage:
		"Olá {{firstname}}, como não tivemos retorno, vamos encerrar este atendimento por aqui. Se precisar, é só chamar de novo.",
	chatbotMinutes: "0",
	chatbotAction: "close",
	chatbotQueueId: "",
	chatbotMessageEnabled: "disabled",
	chatbotMessage:
		"Olá {{firstname}}, como não houve resposta ao atendimento automático, vamos encaminhar você para um atendente.",
	queuedMinutes: "0",
	queuedAction: "close",
	queuedQueueId: "",
	queuedMessageEnabled: "disabled",
	queuedMessage:
		"Olá {{firstname}}, não conseguimos atender você a tempo e pedimos desculpas pela demora. Vamos encerrar este atendimento por aqui, mas é só chamar de novo que retomamos.",
};

const formatDate = (value, texts = getAdminTexts()) => {
	if (!value) return texts.settings.expirationPlaceholder;
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return "-";
	return parsed.toLocaleDateString();
};

const getExpirationValue = expiresAt => {
	if (!expiresAt) return "never";
	const diffDays = Math.round((new Date(expiresAt).getTime() - Date.now()) / 86400000);
	if (diffDays <= 45) return "30d";
	if (diffDays <= 120) return "90d";
	if (diffDays <= 240) return "180d";
	return "1y";
};

const Settings = () => {
	const classes = useStyles();
	const texts = getAdminTexts();
	const localizedExpirationOptions = useMemo(
		() =>
			expirationOptions.map(option => ({
				...option,
				label: texts.settings.expirationOptions[option.value] || option.label,
			})),
		[texts]
	);
	const [settings, setSettings] = useState([]);
	const [loading, setLoading] = useState(true);
	const [awayMinutesDraft, setAwayMinutesDraft] = useState("5");
	const [timezoneDraft, setTimezoneDraft] = useState("America/Sao_Paulo");
	const [tab, setTab] = useState(0);
	const [tokens, setTokens] = useState([]);
	const [tokenSearch, setTokenSearch] = useState("");
	const [tokenModalOpen, setTokenModalOpen] = useState(false);
	const [editingToken, setEditingToken] = useState(null);
	const [tokenName, setTokenName] = useState("");
	const [tokenExpiration, setTokenExpiration] = useState("");
	const [tokenScope, setTokenScope] = useState("full");
	const [savingToken, setSavingToken] = useState(false);
	const [newTokenValue, setNewTokenValue] = useState("");
	const [tokenMenuAnchor, setTokenMenuAnchor] = useState(null);
	const [selectedToken, setSelectedToken] = useState(null);
	const [transcription, setTranscription] = useState(transcriptionDefaults);
	const [savingTranscription, setSavingTranscription] = useState(false);
	const [rating, setRating] = useState(ratingDefaults);
	const [savingRating, setSavingRating] = useState(false);
	const [messaging, setMessaging] = useState(messagingDefaults);
	const [savingMessaging, setSavingMessaging] = useState(false);
	const [timeouts, setTimeouts] = useState(timeoutDefaults);
	const [savingTimeouts, setSavingTimeouts] = useState(false);
	const [queues, setQueues] = useState([]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const [{ data: settingsData }, { data: tokenData }] = await Promise.all([
					api.get("/settings"),
					api.get("/integration-tokens"),
				]);
				setSettings(Array.isArray(settingsData) ? settingsData : []);
				setTokens(Array.isArray(tokenData) ? tokenData : []);

				// Filas em chamada separada: se falhar, o resto da tela continua
				// utilizavel - só o seletor de fila do timeout fica vazio.
				try {
					const { data: queueData } = await api.get("/queue");
					setQueues(Array.isArray(queueData) ? queueData : []);
				} catch (queueErr) {
					setQueues([]);
				}
			} catch (err) {
				toastError(err);
			} finally {
				setLoading(false);
			}
		};
		fetchData();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		socket.on("settings", data => {
			if (data.action === "update") {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					if (settingIndex !== -1) {
						aux[settingIndex].value = data.setting.value;
						return aux;
					}

					return [data.setting, ...aux];
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	useEffect(() => {
		setAwayMinutesDraft(getSettingValue("userAwayMinutes") || "5");
		setTimezoneDraft(getSettingValue("companyTimezone") || "America/Sao_Paulo");
		setTranscription(prev => {
			const provider = getSettingValue("audioTranscriptionProvider") || prev.provider || "openai";
			const fallbackModel = provider === "openai" ? "whisper-1" : "";
			return {
				enabled:
					getSettingValue("audioTranscriptionEnabled") ||
					prev.enabled ||
					transcriptionDefaults.enabled,
				provider,
				apiKey: "",
				model:
					getSettingValue("audioTranscriptionModel") ||
					prev.model ||
					fallbackModel,
				endpoint: getSettingValue("audioTranscriptionEndpoint") || prev.endpoint || "",
				language:
					getSettingValue("audioTranscriptionLanguage") ||
					prev.language ||
					transcriptionDefaults.language,
			};
		});
		setRating(prev => ({
			enabled: getSettingValue("ticketRatingEnabled") || prev.enabled || ratingDefaults.enabled,
			message: getSettingValue("ticketRatingMessage") || prev.message || ratingDefaults.message,
			thankYouMessage:
				getSettingValue("ticketRatingThankYouMessage") ||
				prev.thankYouMessage ||
				ratingDefaults.thankYouMessage,
			allowComment:
				getSettingValue("ticketRatingAllowComment") ||
				prev.allowComment ||
				ratingDefaults.allowComment,
			expiresHours:
				getSettingValue("ticketRatingExpiresHours") ||
				prev.expiresHours ||
				ratingDefaults.expiresHours,
		}));
		setMessaging(prev => ({
			acceptedEnabled:
				getSettingValue("ticketAcceptedMessageEnabled") ||
				prev.acceptedEnabled ||
				messagingDefaults.acceptedEnabled,
			acceptedMessage:
				getSettingValue("ticketAcceptedMessage") ||
				prev.acceptedMessage ||
				messagingDefaults.acceptedMessage,
			transferToUserEnabled:
				getSettingValue("ticketTransferToUserMessageEnabled") ||
				prev.transferToUserEnabled ||
				messagingDefaults.transferToUserEnabled,
			transferToUserMessage:
				getSettingValue("ticketTransferToUserMessage") ||
				prev.transferToUserMessage ||
				messagingDefaults.transferToUserMessage,
			transferToQueueEnabled:
				getSettingValue("ticketTransferToQueueMessageEnabled") ||
				prev.transferToQueueEnabled ||
				messagingDefaults.transferToQueueEnabled,
			transferToQueueMessage:
				getSettingValue("ticketTransferToQueueMessage") ||
				prev.transferToQueueMessage ||
				messagingDefaults.transferToQueueMessage,
			storeAsNote:
				getSettingValue("ticketMessagingStoreAsNote") ||
				prev.storeAsNote ||
				messagingDefaults.storeAsNote,
		}));
		setTimeouts(prev => ({
			noQueueMinutes:
				getSettingValue("ticketNoQueueTimeoutMinutes") ||
				prev.noQueueMinutes ||
				timeoutDefaults.noQueueMinutes,
			noQueueAction:
				getSettingValue("ticketNoQueueTimeoutAction") ||
				prev.noQueueAction ||
				timeoutDefaults.noQueueAction,
			noQueueQueueId:
				getSettingValue("ticketNoQueueTimeoutQueueId") ||
				prev.noQueueQueueId ||
				timeoutDefaults.noQueueQueueId,
			openMinutes:
				getSettingValue("ticketOpenTimeoutMinutes") ||
				prev.openMinutes ||
				timeoutDefaults.openMinutes,
			noQueueMessageEnabled:
				getSettingValue("ticketNoQueueTimeoutMessageEnabled") ||
				prev.noQueueMessageEnabled ||
				timeoutDefaults.noQueueMessageEnabled,
			noQueueMessage:
				getSettingValue("ticketNoQueueTimeoutMessage") ||
				prev.noQueueMessage ||
				timeoutDefaults.noQueueMessage,
			openAction:
				getSettingValue("ticketOpenTimeoutAction") ||
				prev.openAction ||
				timeoutDefaults.openAction,
			openMessageEnabled:
				getSettingValue("ticketOpenTimeoutMessageEnabled") ||
				prev.openMessageEnabled ||
				timeoutDefaults.openMessageEnabled,
			openMessage:
				getSettingValue("ticketOpenTimeoutMessage") ||
				prev.openMessage ||
				timeoutDefaults.openMessage,
			chatbotMinutes:
				getSettingValue("ticketChatbotTimeoutMinutes") ||
				prev.chatbotMinutes ||
				timeoutDefaults.chatbotMinutes,
			chatbotAction:
				getSettingValue("ticketChatbotTimeoutAction") ||
				prev.chatbotAction ||
				timeoutDefaults.chatbotAction,
			chatbotQueueId:
				getSettingValue("ticketChatbotTimeoutQueueId") ||
				prev.chatbotQueueId ||
				timeoutDefaults.chatbotQueueId,
			chatbotMessageEnabled:
				getSettingValue("ticketChatbotTimeoutMessageEnabled") ||
				prev.chatbotMessageEnabled ||
				timeoutDefaults.chatbotMessageEnabled,
			chatbotMessage:
				getSettingValue("ticketChatbotTimeoutMessage") ||
				prev.chatbotMessage ||
				timeoutDefaults.chatbotMessage,
			queuedMinutes:
				getSettingValue("ticketQueuedTimeoutMinutes") ||
				prev.queuedMinutes ||
				timeoutDefaults.queuedMinutes,
			queuedAction:
				getSettingValue("ticketQueuedTimeoutAction") ||
				prev.queuedAction ||
				timeoutDefaults.queuedAction,
			queuedQueueId:
				getSettingValue("ticketQueuedTimeoutQueueId") ||
				prev.queuedQueueId ||
				timeoutDefaults.queuedQueueId,
			queuedMessageEnabled:
				getSettingValue("ticketQueuedTimeoutMessageEnabled") ||
				prev.queuedMessageEnabled ||
				timeoutDefaults.queuedMessageEnabled,
			queuedMessage:
				getSettingValue("ticketQueuedTimeoutMessage") ||
				prev.queuedMessage ||
				timeoutDefaults.queuedMessage,
		}));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [settings]);

	const getSettingValue = key => {
		return settings.find(s => s.key === key)?.value;
	};

	const filteredTokens = useMemo(() => {
		const search = tokenSearch.trim().toLowerCase();
		if (!search) return tokens;
		return tokens.filter(token => String(token.name || "").toLowerCase().includes(search));
	}, [tokens, tokenSearch]);

	const upsertToken = token => {
		setTokens(prev => {
			const index = prev.findIndex(item => item.id === token.id);
			if (index === -1) return [token, ...prev];
			const copy = [...prev];
			copy[index] = token;
			return copy;
		});
	};

	const handleSaveAwayMinutes = async () => {
		const sanitizedValue = String(Math.max(1, Number(awayMinutesDraft) || 5));

		if (sanitizedValue === (getSettingValue("userAwayMinutes") || "5")) {
			setAwayMinutesDraft(sanitizedValue);
			return;
		}

		try {
			await api.put("/settings/userAwayMinutes", { value: sanitizedValue });
			setAwayMinutesDraft(sanitizedValue);
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleSaveTimezone = async value => {
		const sanitizedValue = value || "America/Sao_Paulo";

		if (sanitizedValue === (getSettingValue("companyTimezone") || "America/Sao_Paulo")) {
			setTimezoneDraft(sanitizedValue);
			return;
		}

		try {
			await api.put("/settings/companyTimezone", { value: sanitizedValue });
			setTimezoneDraft(sanitizedValue);
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleChangeTranscription = field => event => {
		const value = event.target.value;
		setTranscription(prev => {
			const next = { ...prev, [field]: value };
			if (field === "provider" && value === "openai" && !next.model) {
				next.model = "whisper-1";
			}
			return next;
		});
	};

	const handleSaveTranscription = async () => {
		if (transcription.enabled === "enabled" && !transcription.provider) {
			toast.error("Escolha o provedor de transcrição.");
			return;
		}

		if (transcription.enabled === "enabled" && !transcription.model.trim()) {
			toast.error(texts.settings.modelValidation);
			return;
		}

		setSavingTranscription(true);
		try {
			const updates = [
				["audioTranscriptionEnabled", transcription.enabled],
				["audioTranscriptionProvider", transcription.provider],
				["audioTranscriptionModel", transcription.model.trim()],
				["audioTranscriptionEndpoint", transcription.endpoint.trim()],
				["audioTranscriptionLanguage", transcription.language.trim()],
			];

			if (transcription.apiKey.trim()) {
				updates.push(["audioTranscriptionApiKey", transcription.apiKey.trim()]);
			}

			await Promise.all(
				updates.map(([key, value]) => api.put(`/settings/${key}`, { value }))
			);
			setTranscription(prev => ({ ...prev, apiKey: "" }));
			toast.success("Configuração de transcrição salva.");
		} catch (err) {
			toastError(err);
		} finally {
			setSavingTranscription(false);
		}
	};

	const handleChangeRating = field => event => {
		const value =
			event?.target?.type === "checkbox"
				? event.target.checked
					? "enabled"
					: "disabled"
				: event.target.value;
		setRating(prev => ({ ...prev, [field]: value }));
	};

	const handleSaveRating = async () => {
		const expiresHours = String(Math.max(1, Number(rating.expiresHours) || 24));

		if (rating.enabled === "enabled" && !rating.message.trim()) {
			toast.error("Informe a mensagem de avaliação.");
			return;
		}

		setSavingRating(true);
		try {
			const updates = [
				["ticketRatingEnabled", rating.enabled],
				["ticketRatingMessage", rating.message.trim()],
				["ticketRatingThankYouMessage", rating.thankYouMessage.trim()],
				["ticketRatingAllowComment", rating.allowComment],
				["ticketRatingExpiresHours", expiresHours],
			];

			await Promise.all(
				updates.map(([key, value]) => api.put(`/settings/${key}`, { value }))
			);
			setRating(prev => ({ ...prev, expiresHours }));
			toast.success("Configuração de avaliação salva.");
		} catch (err) {
			toastError(err);
		} finally {
			setSavingRating(false);
		}
	};

	const handleChangeMessaging = field => event => {
		const value =
			event?.target?.type === "checkbox"
				? event.target.checked
					? "enabled"
					: "disabled"
				: event.target.value;
		setMessaging(prev => ({ ...prev, [field]: value }));
	};

	const handleSaveMessaging = async () => {
		if (messaging.acceptedEnabled === "enabled" && !messaging.acceptedMessage.trim()) {
			toast.error("Informe a mensagem de ticket aceito.");
			return;
		}
		if (
			messaging.transferToUserEnabled === "enabled" &&
			!messaging.transferToUserMessage.trim()
		) {
			toast.error("Informe a mensagem de transferência para atendente.");
			return;
		}
		if (
			messaging.transferToQueueEnabled === "enabled" &&
			!messaging.transferToQueueMessage.trim()
		) {
			toast.error("Informe a mensagem de transferência para fila.");
			return;
		}

		setSavingMessaging(true);
		try {
			const updates = [
				["ticketAcceptedMessageEnabled", messaging.acceptedEnabled],
				["ticketAcceptedMessage", messaging.acceptedMessage.trim()],
				["ticketTransferToUserMessageEnabled", messaging.transferToUserEnabled],
				["ticketTransferToUserMessage", messaging.transferToUserMessage.trim()],
				["ticketTransferToQueueMessageEnabled", messaging.transferToQueueEnabled],
				["ticketTransferToQueueMessage", messaging.transferToQueueMessage.trim()],
				["ticketMessagingStoreAsNote", messaging.storeAsNote],
			];

			await Promise.all(
				updates.map(([key, value]) => api.put(`/settings/${key}`, { value }))
			);
			toast.success("Configuração de mensagens salva.");
		} catch (err) {
			toastError(err);
		} finally {
			setSavingMessaging(false);
		}
	};

	const handleChangeTimeouts = field => event => {
		const value =
			event?.target?.type === "checkbox"
				? event.target.checked
					? "enabled"
					: "disabled"
				: event.target.value;
		setTimeouts(prev => ({ ...prev, [field]: value }));
	};

	const handleSaveTimeouts = async () => {
		const noQueueMinutes = String(Math.max(0, Number(timeouts.noQueueMinutes) || 0));
		const openMinutes = String(Math.max(0, Number(timeouts.openMinutes) || 0));
		const chatbotMinutes = String(Math.max(0, Number(timeouts.chatbotMinutes) || 0));
		const queuedMinutes = String(Math.max(0, Number(timeouts.queuedMinutes) || 0));

		if (
			Number(chatbotMinutes) > 0 &&
			timeouts.chatbotAction === "transfer" &&
			!timeouts.chatbotQueueId
		) {
			toast.error("Escolha a fila de destino para o timeout de ticket com chatbot.");
			return;
		}
		if (
			timeouts.chatbotMessageEnabled === "enabled" &&
			!timeouts.chatbotMessage.trim()
		) {
			toast.error("Informe a mensagem do timeout de ticket com chatbot.");
			return;
		}
		if (
			Number(queuedMinutes) > 0 &&
			timeouts.queuedAction === "transfer" &&
			!timeouts.queuedQueueId
		) {
			toast.error("Escolha a fila de destino para o timeout de ticket aguardando em fila.");
			return;
		}
		if (
			timeouts.queuedMessageEnabled === "enabled" &&
			!timeouts.queuedMessage.trim()
		) {
			toast.error("Informe a mensagem do timeout de ticket aguardando em fila.");
			return;
		}

		// Transferir para fila exige uma fila: sem isso a regra ficaria ligada e
		// silenciosamente sem efeito, que é o pior dos dois mundos.
		if (
			Number(noQueueMinutes) > 0 &&
			timeouts.noQueueAction === "transfer" &&
			!timeouts.noQueueQueueId
		) {
			toast.error("Escolha a fila de destino para o timeout de ticket sem fila.");
			return;
		}

		if (
			timeouts.noQueueMessageEnabled === "enabled" &&
			!timeouts.noQueueMessage.trim()
		) {
			toast.error("Informe a mensagem do timeout de ticket sem fila.");
			return;
		}
		if (timeouts.openMessageEnabled === "enabled" && !timeouts.openMessage.trim()) {
			toast.error("Informe a mensagem do timeout de ticket em atendimento.");
			return;
		}

		setSavingTimeouts(true);
		try {
			const updates = [
				["ticketNoQueueTimeoutMinutes", noQueueMinutes],
				["ticketNoQueueTimeoutAction", timeouts.noQueueAction],
				["ticketNoQueueTimeoutQueueId", String(timeouts.noQueueQueueId || "")],
				["ticketNoQueueTimeoutMessageEnabled", timeouts.noQueueMessageEnabled],
				["ticketNoQueueTimeoutMessage", timeouts.noQueueMessage.trim()],
				["ticketOpenTimeoutMinutes", openMinutes],
				["ticketOpenTimeoutAction", timeouts.openAction],
				["ticketOpenTimeoutMessageEnabled", timeouts.openMessageEnabled],
				["ticketOpenTimeoutMessage", timeouts.openMessage.trim()],
				["ticketChatbotTimeoutMinutes", chatbotMinutes],
				["ticketChatbotTimeoutAction", timeouts.chatbotAction],
				["ticketChatbotTimeoutQueueId", String(timeouts.chatbotQueueId || "")],
				["ticketChatbotTimeoutMessageEnabled", timeouts.chatbotMessageEnabled],
				["ticketChatbotTimeoutMessage", timeouts.chatbotMessage.trim()],
				["ticketQueuedTimeoutMinutes", queuedMinutes],
				["ticketQueuedTimeoutAction", timeouts.queuedAction],
				["ticketQueuedTimeoutQueueId", String(timeouts.queuedQueueId || "")],
				["ticketQueuedTimeoutMessageEnabled", timeouts.queuedMessageEnabled],
				["ticketQueuedTimeoutMessage", timeouts.queuedMessage.trim()],
			];

			await Promise.all(
				updates.map(([key, value]) => api.put(`/settings/${key}`, { value }))
			);
			setTimeouts(prev => ({
				...prev,
				noQueueMinutes,
				openMinutes,
				chatbotMinutes,
				queuedMinutes,
			}));
			toast.success("Configuração de tempos de espera salva.");
		} catch (err) {
			toastError(err);
		} finally {
			setSavingTimeouts(false);
		}
	};

	const openCreateTokenModal = () => {
		setEditingToken(null);
		setTokenName("");
		setTokenExpiration("");
		setTokenScope("full");
		setNewTokenValue("");
		setTokenModalOpen(true);
	};

	const openEditTokenModal = token => {
		setEditingToken(token);
		setTokenName(token.name || "");
		setTokenExpiration(getExpirationValue(token.expiresAt));
		setNewTokenValue("");
		setTokenModalOpen(true);
		setTokenMenuAnchor(null);
	};

	const handleSaveToken = async () => {
		if (!tokenName.trim() || !tokenExpiration) {
			toast.error(texts.settings.tokenValidation);
			return;
		}

		setSavingToken(true);
		try {
			if (editingToken) {
				const { data } = await api.put(`/integration-tokens/${editingToken.id}`, {
					name: tokenName,
					expiresIn: tokenExpiration,
				});
				upsertToken(data);
				setTokenModalOpen(false);
				toast.success("Token atualizado com sucesso.");
				return;
			}

			const { data } = await api.post("/integration-tokens", {
				name: tokenName,
				expiresIn: tokenExpiration,
				scope: tokenScope,
			});
			upsertToken(data);
			setNewTokenValue(data.token || "");
			toast.success("Token gerado com sucesso.");
		} catch (err) {
			toastError(err);
		} finally {
			setSavingToken(false);
		}
	};

	const handleRevokeToken = async token => {
		if (!token) return;
		try {
			const { data } = await api.post(`/integration-tokens/${token.id}/revoke`);
			upsertToken(data);
			toast.success("Token revogado com sucesso.");
		} catch (err) {
			toastError(err);
		} finally {
			setTokenMenuAnchor(null);
			setSelectedToken(null);
		}
	};

	const handleCopyNewToken = async () => {
		try {
			await navigator.clipboard.writeText(newTokenValue);
			toast.success("Token copiado.");
		} catch (err) {
			toast.error("Não foi possível copiar o token.");
		}
	};

	const statusConfig = {
		active: { label: texts.settings.active, color: "#22c55e" },
		revoked: { label: texts.settings.revoked, color: "#ef4444" },
		expired: { label: texts.settings.expired, color: "#f97316" },
	};

	return (
		<div className={classes.root}>
			<Container className={classes.container} maxWidth={false}>
				<div className={classes.header}>
					<Typography variant="h6">{i18n.t("settings.title")}</Typography>
				</div>

				<Paper className={classes.tabsPaper}>
					<Tabs
						value={tab}
						onChange={(_, value) => setTab(value)}
						indicatorColor="primary"
						textColor="primary"
					>
						<Tab label={texts.settings.general} />
						<Tab label={texts.settings.integrations} />
						<Tab label="Avaliações" />
						<Tab label="Mensagens de atendimento" />
						<Tab label="Tempos de espera" />
					</Tabs>
				</Paper>

				{tab === 0 && (
					<>
						<Paper className={classes.paper}>
							<TextField
								id="user-away-minutes-setting"
								label={texts.settings.awayMinutes}
								margin="dense"
								variant="outlined"
								type="number"
								fullWidth
								inputProps={{ min: 1 }}
								helperText={texts.settings.awayMinutesHelp}
								value={awayMinutesDraft}
								onChange={e => setAwayMinutesDraft(e.target.value)}
								onBlur={handleSaveAwayMinutes}
								onKeyDown={e => {
									if (e.key === "Enter") handleSaveAwayMinutes();
								}}
								disabled={loading}
							/>
						</Paper>

						<Paper className={classes.paper}>
							<TextField
								id="company-timezone-setting"
								select
								label={texts.settings.dashboardTimezone}
								margin="dense"
								variant="outlined"
								fullWidth
								helperText={texts.settings.dashboardTimezoneHelp}
								value={timezoneDraft}
								onChange={e => {
									setTimezoneDraft(e.target.value);
									handleSaveTimezone(e.target.value);
								}}
								disabled={loading}
							>
								{timezoneOptions.map(option => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</TextField>
						</Paper>
					</>
				)}

				{tab === 1 && (
					<>
						<Paper className={classes.integrationSection} variant="outlined">
							<div className={classes.sectionHeader}>
								<div>
									<Typography variant="subtitle1">{texts.settings.transcriptionTitle}</Typography>
									<Typography variant="body2" color="textSecondary">
										{texts.settings.transcriptionSubtitle}
									</Typography>
								</div>
								<Button
									color="primary"
									variant="contained"
									onClick={handleSaveTranscription}
									disabled={savingTranscription || loading}
								>
									{savingTranscription ? texts.common.saving : texts.settings.saveTranscription}
								</Button>
							</div>

							<div className={classes.transcriptionGrid}>
								<TextField
									select
									variant="outlined"
									size="small"
									label={texts.common.status}
									value={transcription.enabled}
									onChange={handleChangeTranscription("enabled")}
								>
									<MenuItem value="disabled">{texts.settings.disabled}</MenuItem>
									<MenuItem value="enabled">{texts.settings.enabled}</MenuItem>
								</TextField>
								<TextField
									select
									variant="outlined"
									size="small"
									label={texts.settings.transcriptionAi}
									value={transcription.provider}
									onChange={handleChangeTranscription("provider")}
								>
									<MenuItem value="openai">OpenAI</MenuItem>
									<MenuItem value="grok">Grok / xAI</MenuItem>
								</TextField>
								<TextField
									variant="outlined"
									size="small"
									label={texts.settings.model}
									placeholder={transcription.provider === "openai" ? "whisper-1" : texts.settings.providerModelPlaceholder}
									value={transcription.model}
									onChange={handleChangeTranscription("model")}
									helperText={texts.settings.modelHelp}
								/>
								<TextField
									variant="outlined"
									size="small"
									label={texts.settings.language}
									placeholder="pt"
									value={transcription.language}
									onChange={handleChangeTranscription("language")}
									helperText={texts.settings.languageHelp}
								/>
								<TextField
									variant="outlined"
									size="small"
									label={texts.settings.customEndpoint}
									placeholder={
										transcription.provider === "openai"
											? "https://api.openai.com/v1/audio/transcriptions"
											: "https://api.x.ai/v1/audio/transcriptions"
									}
									value={transcription.endpoint}
									onChange={handleChangeTranscription("endpoint")}
									helperText={texts.settings.endpointHelp}
								/>
								<TextField
									variant="outlined"
									size="small"
									type="password"
									label={texts.settings.apiKey}
									value={transcription.apiKey}
									onChange={handleChangeTranscription("apiKey")}
									helperText={texts.settings.apiKeyHelp}
								/>
							</div>
						</Paper>

						<div className={classes.integrationsToolbar}>
							<TextField
								className={classes.searchField}
								variant="outlined"
								size="small"
								placeholder={texts.settings.searchToken}
								value={tokenSearch}
								onChange={e => setTokenSearch(e.target.value)}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon fontSize="small" />
										</InputAdornment>
									),
								}}
							/>
							<Button
								className={classes.generateButton}
								color="primary"
								variant="contained"
								onClick={openCreateTokenModal}
							>
								{texts.settings.generateToken}
							</Button>
						</div>

						<Paper className={classes.tablePaper}>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell>{texts.settings.tokenName}</TableCell>
										<TableCell>{texts.settings.token}</TableCell>
										<TableCell>{texts.settings.expirationDate}</TableCell>
										<TableCell>{texts.common.status}</TableCell>
										<TableCell align="right">{texts.common.actions}</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{filteredTokens.map(token => {
										const status = statusConfig[token.status] || statusConfig.active;
										return (
											<TableRow key={token.id}>
												<TableCell>
													{token.name}
													{token.scope === "webchat_links" ? (
														<Typography variant="caption" color="textSecondary" display="block">
															Só chat do app
														</Typography>
													) : null}
												</TableCell>
												<TableCell>{token.tokenPreview}</TableCell>
												<TableCell>{formatDate(token.expiresAt, texts)}</TableCell>
												<TableCell>
													<Chip
														size="small"
														className={classes.statusChip}
														label={status.label}
														style={{
															color: status.color,
															borderColor: `${status.color}55`,
															backgroundColor: `${status.color}10`,
														}}
														variant="outlined"
													/>
												</TableCell>
												<TableCell align="right">
													<IconButton
														size="small"
														onClick={event => {
															setSelectedToken(token);
															setTokenMenuAnchor(event.currentTarget);
														}}
													>
														<MoreVertIcon fontSize="small" />
													</IconButton>
												</TableCell>
											</TableRow>
										);
									})}
									{filteredTokens.length === 0 && (
										<TableRow>
											<TableCell colSpan={5}>
												<div className={classes.emptyState}>
													{texts.settings.noTokens}
												</div>
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</Paper>
					</>
				)}

				{tab === 2 && (
					<Paper className={classes.integrationSection} variant="outlined">
						<div className={classes.sectionHeader}>
							<div>
								<Typography variant="subtitle1">Pesquisa de satisfação</Typography>
								<Typography variant="body2" color="textSecondary">
									Envie uma avaliação de 1 a 5 automaticamente quando o atendimento for encerrado.
								</Typography>
							</div>
							<Button
								color="primary"
								variant="contained"
								onClick={handleSaveRating}
								disabled={savingRating || loading}
							>
								{savingRating ? texts.common.saving : texts.common.save}
							</Button>
						</div>

						<div className={classes.transcriptionGrid}>
							<FormControlLabel
								control={
									<Switch
										color="primary"
										checked={rating.enabled === "enabled"}
										onChange={handleChangeRating("enabled")}
									/>
								}
								label="Enviar avaliação ao encerrar chamado"
							/>
							<FormControlLabel
								control={
									<Switch
										color="primary"
										checked={rating.allowComment === "enabled"}
										onChange={handleChangeRating("allowComment")}
									/>
								}
								label="Permitir comentario junto com a nota"
							/>
							<TextField
								variant="outlined"
								size="small"
								type="number"
								label="Prazo para resposta (horas)"
								value={rating.expiresHours}
								onChange={handleChangeRating("expiresHours")}
								inputProps={{ min: 1, max: 720 }}
							/>
							<TextField
								variant="outlined"
								size="small"
								label="Tipo de avaliação"
								value="Nota de 1 a 5"
								disabled
							/>
						</div>

						<TextField
							className={classes.formField}
							variant="outlined"
							label="Mensagem da avaliação"
							fullWidth
							multiline
							minRows={3}
							value={rating.message}
							onChange={handleChangeRating("message")}
							helperText="Ex.: responda com uma nota de 1 a 5."
						/>
						<TextField
							className={classes.formField}
							variant="outlined"
							label="Mensagem após resposta"
							fullWidth
							multiline
							minRows={2}
							value={rating.thankYouMessage}
							onChange={handleChangeRating("thankYouMessage")}
						/>
					</Paper>
				)}

				{tab === 3 && (
					<Paper className={classes.integrationSection} variant="outlined">
						<div className={classes.sectionHeader}>
							<div>
								<Typography variant="subtitle1">Mensagens de atendimento</Typography>
								<Typography variant="body2" color="textSecondary">
									Envie uma mensagem automática ao cliente quando o ticket for aceito ou transferido.
									A transferência para um atendente específico e a transferência para uma fila (sem
									dono) são situações diferentes, então têm mensagem própria cada uma.
									Variáveis disponíveis: {"{{firstname}}"} {"{{name}}"} {"{{user}}"} {"{{queue}}"}
								</Typography>
							</div>
							<Button
								color="primary"
								variant="contained"
								onClick={handleSaveMessaging}
								disabled={savingMessaging || loading}
							>
								{savingMessaging ? texts.common.saving : texts.common.save}
							</Button>
						</div>

						<div className={classes.transcriptionGrid}>
							<FormControlLabel
								control={
									<Switch
										color="primary"
										checked={messaging.acceptedEnabled === "enabled"}
										onChange={handleChangeMessaging("acceptedEnabled")}
									/>
								}
								label="Enviar mensagem de ticket aceito"
							/>
							<FormControlLabel
								control={
									<Switch
										color="primary"
										checked={messaging.transferToUserEnabled === "enabled"}
										onChange={handleChangeMessaging("transferToUserEnabled")}
									/>
								}
								label="Enviar mensagem de transferência para atendente"
							/>
							<FormControlLabel
								control={
									<Switch
										color="primary"
										checked={messaging.transferToQueueEnabled === "enabled"}
										onChange={handleChangeMessaging("transferToQueueEnabled")}
									/>
								}
								label="Enviar mensagem de transferência para fila"
							/>
						</div>

						<TextField
							className={classes.formField}
							variant="outlined"
							label="Mensagem de ticket aceito"
							fullWidth
							multiline
							minRows={3}
							placeholder="Digite sua mensagem de ticket aceito aqui"
							value={messaging.acceptedMessage}
							onChange={handleChangeMessaging("acceptedMessage")}
						/>
						<TextField
							className={classes.formField}
							variant="outlined"
							label="Mensagem de transferência para atendente"
							fullWidth
							multiline
							minRows={3}
							placeholder="Digite sua mensagem de transferência para atendente aqui"
							helperText="Disparada quando o ticket é atribuído a um atendente específico."
							value={messaging.transferToUserMessage}
							onChange={handleChangeMessaging("transferToUserMessage")}
						/>
						<TextField
							className={classes.formField}
							variant="outlined"
							label="Mensagem de transferência para fila"
							fullWidth
							multiline
							minRows={3}
							placeholder="Digite sua mensagem de transferência para fila aqui"
							helperText="Disparada quando o ticket é movido para uma fila sem atendente definido, seja porque foi roteado para lá ou porque um atendente devolveu o ticket para a fila."
							value={messaging.transferToQueueMessage}
							onChange={handleChangeMessaging("transferToQueueMessage")}
						/>

						<FormControlLabel
							control={
								<Switch
									color="primary"
									checked={messaging.storeAsNote === "enabled"}
									onChange={handleChangeMessaging("storeAsNote")}
								/>
							}
							label="Armazenar mensagens de aceite e transferência como anotações"
						/>
						<Typography variant="caption" color="textSecondary">
							Além de enviadas ao cliente, o texto exato fica registrado como anotação interna do
							ticket (visível só para atendentes).
						</Typography>
					</Paper>
				)}

				{tab === 4 && (
					<>
						<Paper className={classes.integrationSection} variant="outlined">
							<div className={classes.sectionHeader}>
								<div>
									<Typography variant="subtitle1">Tempos de espera</Typography>
									<Typography variant="body2" color="textSecondary">
										Age sobre tickets parados há muito tempo, contando a partir da última
										mensagem trocada. Cada regra abaixo é independente: deixe em 0 para
										desativar. Um ticket recebe no máximo uma ação por vez — quando ele se
										encaixa em mais de uma regra, vale a mais específica, e a do chatbot é
										avaliada primeiro.
									</Typography>
									<Typography variant="body2" color="textSecondary">
										Anotações internas e marcadores do sistema não contam como conversa. Com o
										aviso ao cliente ligado, os envios são espaçados e limitados por varredura
										para um acervo represado não virar disparo em massa — nesse caso a fila é
										drenada aos poucos, ao longo de vários minutos.
									</Typography>
								</div>
								<Button
									color="primary"
									variant="contained"
									onClick={handleSaveTimeouts}
									disabled={savingTimeouts || loading}
								>
									{savingTimeouts ? texts.common.saving : texts.common.save}
								</Button>
							</div>
						</Paper>

						{/* 1. Chatbot */}
						<Paper className={classes.ruleCard} variant="outlined">
							<Typography variant="subtitle2" className={classes.ruleTitle}>
								Ticket com chatbot
							</Typography>
							<Typography variant="caption" className={classes.ruleDescription}>
								Conta como &quot;com chatbot&quot; quando a conexão tem n8n ou Typebot ativo e o
								bot ainda está no controle: automação não pausada e, se já há atendente, a opção
								&quot;Interromper quando o chamado for aceito&quot; desligada na conexão.
							</Typography>

							<div className={classes.transcriptionGrid}>
								<TextField
									variant="outlined"
									size="small"
									type="number"
									label="Timeout (minutos)"
									value={timeouts.chatbotMinutes}
									onChange={handleChangeTimeouts("chatbotMinutes")}
									inputProps={{ min: 0, max: 43200 }}
									helperText="0 desativa esta regra."
								/>
								<TextField
									select
									variant="outlined"
									size="small"
									label="Ação"
									value={timeouts.chatbotAction}
									onChange={handleChangeTimeouts("chatbotAction")}
									disabled={Number(timeouts.chatbotMinutes) <= 0}
								>
									<MenuItem value="close">Fechar ticket</MenuItem>
									<MenuItem value="transfer">Transferir para fila</MenuItem>
								</TextField>
								<TextField
									select
									variant="outlined"
									size="small"
									label="Fila de destino"
									value={timeouts.chatbotQueueId}
									onChange={handleChangeTimeouts("chatbotQueueId")}
									disabled={
										Number(timeouts.chatbotMinutes) <= 0 ||
										timeouts.chatbotAction !== "transfer"
									}
								>
									<MenuItem value="">&nbsp;</MenuItem>
									{queues.map(queue => (
										<MenuItem key={queue.id} value={String(queue.id)}>
											{queue.name}
										</MenuItem>
									))}
								</TextField>
							</div>

							<FormControlLabel
								control={
									<Switch
										color="primary"
										checked={timeouts.chatbotMessageEnabled === "enabled"}
										onChange={handleChangeTimeouts("chatbotMessageEnabled")}
										disabled={Number(timeouts.chatbotMinutes) <= 0}
									/>
								}
								label="Avisar o cliente antes de aplicar a ação"
							/>
							<TextField
								className={classes.formField}
								variant="outlined"
								label="Mensagem enviada ao cliente"
								fullWidth
								multiline
								minRows={2}
								value={timeouts.chatbotMessage}
								onChange={handleChangeTimeouts("chatbotMessage")}
								disabled={
									Number(timeouts.chatbotMinutes) <= 0 ||
									timeouts.chatbotMessageEnabled !== "enabled"
								}
								helperText="Enviada logo antes de fechar ou transferir."
							/>
						</Paper>

						{/* 2. Sem fila */}
						<Paper className={classes.ruleCard} variant="outlined">
							<Typography variant="subtitle2" className={classes.ruleTitle}>
								Ticket sem fila
							</Typography>
							<Typography variant="caption" className={classes.ruleDescription}>
								Aguardando e sem fila definida — o ticket não foi roteado para lugar nenhum e
								ninguém o vê numa fila.
							</Typography>

							<div className={classes.transcriptionGrid}>
								<TextField
									variant="outlined"
									size="small"
									type="number"
									label="Timeout (minutos)"
									value={timeouts.noQueueMinutes}
									onChange={handleChangeTimeouts("noQueueMinutes")}
									inputProps={{ min: 0, max: 43200 }}
									helperText="0 desativa esta regra."
								/>
								<TextField
									select
									variant="outlined"
									size="small"
									label="Ação"
									value={timeouts.noQueueAction}
									onChange={handleChangeTimeouts("noQueueAction")}
									disabled={Number(timeouts.noQueueMinutes) <= 0}
								>
									<MenuItem value="close">Fechar ticket</MenuItem>
									<MenuItem value="transfer">Transferir para fila</MenuItem>
								</TextField>
								<TextField
									select
									variant="outlined"
									size="small"
									label="Fila de destino"
									value={timeouts.noQueueQueueId}
									onChange={handleChangeTimeouts("noQueueQueueId")}
									disabled={
										Number(timeouts.noQueueMinutes) <= 0 ||
										timeouts.noQueueAction !== "transfer"
									}
								>
									<MenuItem value="">&nbsp;</MenuItem>
									{queues.map(queue => (
										<MenuItem key={queue.id} value={String(queue.id)}>
											{queue.name}
										</MenuItem>
									))}
								</TextField>
							</div>

							<FormControlLabel
								control={
									<Switch
										color="primary"
										checked={timeouts.noQueueMessageEnabled === "enabled"}
										onChange={handleChangeTimeouts("noQueueMessageEnabled")}
										disabled={Number(timeouts.noQueueMinutes) <= 0}
									/>
								}
								label="Avisar o cliente antes de aplicar a ação"
							/>
							<TextField
								className={classes.formField}
								variant="outlined"
								label="Mensagem enviada ao cliente"
								fullWidth
								multiline
								minRows={2}
								value={timeouts.noQueueMessage}
								onChange={handleChangeTimeouts("noQueueMessage")}
								disabled={
									Number(timeouts.noQueueMinutes) <= 0 ||
									timeouts.noQueueMessageEnabled !== "enabled"
								}
								helperText="Substitui a mensagem de transferência manual quando a ação é transferir."
							/>
						</Paper>

						{/* 3. Aguardando em fila */}
						<Paper className={classes.ruleCard} variant="outlined">
							<Typography variant="subtitle2" className={classes.ruleTitle}>
								Ticket aguardando em fila
							</Typography>
							<Typography variant="caption" className={classes.ruleDescription}>
								Roteado para uma fila e ninguém assumiu. Aqui o cliente espera a primeira
								resposta — a demora foi da empresa, não dele.
							</Typography>

							<div className={classes.transcriptionGrid}>
								<TextField
									variant="outlined"
									size="small"
									type="number"
									label="Timeout (minutos)"
									value={timeouts.queuedMinutes}
									onChange={handleChangeTimeouts("queuedMinutes")}
									inputProps={{ min: 0, max: 43200 }}
									helperText="0 desativa esta regra."
								/>
								<TextField
									select
									variant="outlined"
									size="small"
									label="Ação"
									value={timeouts.queuedAction}
									onChange={handleChangeTimeouts("queuedAction")}
									disabled={Number(timeouts.queuedMinutes) <= 0}
								>
									<MenuItem value="close">Fechar ticket</MenuItem>
									<MenuItem value="transfer">Transferir para fila</MenuItem>
								</TextField>
								<TextField
									select
									variant="outlined"
									size="small"
									label="Fila de destino"
									value={timeouts.queuedQueueId}
									onChange={handleChangeTimeouts("queuedQueueId")}
									disabled={
										Number(timeouts.queuedMinutes) <= 0 ||
										timeouts.queuedAction !== "transfer"
									}
								>
									<MenuItem value="">&nbsp;</MenuItem>
									{queues.map(queue => (
										<MenuItem key={queue.id} value={String(queue.id)}>
											{queue.name}
										</MenuItem>
									))}
								</TextField>
							</div>

							<FormControlLabel
								control={
									<Switch
										color="primary"
										checked={timeouts.queuedMessageEnabled === "enabled"}
										onChange={handleChangeTimeouts("queuedMessageEnabled")}
										disabled={Number(timeouts.queuedMinutes) <= 0}
									/>
								}
								label="Avisar o cliente antes de aplicar a ação"
							/>
							<TextField
								className={classes.formField}
								variant="outlined"
								label="Mensagem enviada ao cliente"
								fullWidth
								multiline
								minRows={2}
								value={timeouts.queuedMessage}
								onChange={handleChangeTimeouts("queuedMessage")}
								disabled={
									Number(timeouts.queuedMinutes) <= 0 ||
									timeouts.queuedMessageEnabled !== "enabled"
								}
								helperText="Vale um texto que reconheça a demora, em vez de falar em inatividade."
							/>
						</Paper>

						{/* 4. Em atendimento */}
						<Paper className={classes.ruleCard} variant="outlined">
							<Typography variant="subtitle2" className={classes.ruleTitle}>
								Ticket em atendimento
							</Typography>
							<Typography variant="caption" className={classes.ruleDescription}>
								Só vence o prazo quando a última mensagem foi NOSSA — ou seja, quem parou de
								responder foi o cliente. Se o atendente é que está devendo resposta, o ticket não
								é tocado. &quot;Retornar para a fila&quot; devolve para a própria fila do ticket,
								sem atendente; ticket sem fila definida é ignorado por essa ação, porque não
								haveria para onde devolvê-lo.
							</Typography>

							<div className={classes.transcriptionGrid}>
								<TextField
									variant="outlined"
									size="small"
									type="number"
									label="Timeout (minutos)"
									value={timeouts.openMinutes}
									onChange={handleChangeTimeouts("openMinutes")}
									inputProps={{ min: 0, max: 43200 }}
									helperText="0 desativa esta regra."
								/>
								<TextField
									select
									variant="outlined"
									size="small"
									label="Ação"
									value={timeouts.openAction}
									onChange={handleChangeTimeouts("openAction")}
									disabled={Number(timeouts.openMinutes) <= 0}
								>
									<MenuItem value="close">Fechar ticket</MenuItem>
									<MenuItem value="return">Retornar para a fila</MenuItem>
								</TextField>
							</div>

							{/* Devolver à fila é operação interna: o cliente não perdeu nada e
							    não tem o que fazer com o aviso. Antes o campo ficava visível e o
							    texto padrão dizia "vamos encerrar", avisando de um encerramento
							    que não acontecia — e repetindo a cada ciclo de aceite sem
							    resposta. O backend também ignora o aviso nessa ação. */}
							{timeouts.openAction === "return" ? (
								<Typography variant="caption" color="textSecondary">
									Devolver à fila não avisa o cliente: para ele nada muda, o atendimento
									continua aberto e só troca de responsável. O aviso fica disponível quando a
									ação for &quot;Fechar ticket&quot;.
								</Typography>
							) : (
								<>
									<FormControlLabel
										control={
											<Switch
												color="primary"
												checked={timeouts.openMessageEnabled === "enabled"}
												onChange={handleChangeTimeouts("openMessageEnabled")}
												disabled={Number(timeouts.openMinutes) <= 0}
											/>
										}
										label="Avisar o cliente antes de aplicar a ação"
									/>
									<TextField
										className={classes.formField}
										variant="outlined"
										label="Mensagem enviada ao cliente"
										fullWidth
										multiline
										minRows={2}
										value={timeouts.openMessage}
										onChange={handleChangeTimeouts("openMessage")}
										disabled={
											Number(timeouts.openMinutes) <= 0 ||
											timeouts.openMessageEnabled !== "enabled"
										}
										helperText="Enviada logo antes de fechar o ticket."
									/>
								</>
							)}
						</Paper>
					</>
				)}

				<Menu
					anchorEl={tokenMenuAnchor}
					open={Boolean(tokenMenuAnchor)}
					onClose={() => {
						setTokenMenuAnchor(null);
						setSelectedToken(null);
					}}
				>
					<MenuItem
						onClick={() => openEditTokenModal(selectedToken)}
						disabled={!selectedToken || selectedToken.status === "revoked"}
					>
						<EditIcon className={classes.menuIcon} />
						{texts.common.edit}
					</MenuItem>
					<MenuItem
						className={classes.dangerItem}
						onClick={() => handleRevokeToken(selectedToken)}
						disabled={!selectedToken || selectedToken.status === "revoked"}
					>
						<DeleteOutlineIcon className={classes.menuIcon} />
						{texts.settings.revoke}
					</MenuItem>
				</Menu>

				<Dialog
					open={tokenModalOpen}
					onClose={() => setTokenModalOpen(false)}
					maxWidth="sm"
					fullWidth
					classes={{ paper: classes.dialogPaper }}
				>
					<DialogTitle disableTypography>
						<div className={classes.dialogTitle}>
							<Typography variant="h6">
								{editingToken ? texts.settings.editToken : texts.settings.generateToken}
							</Typography>
							<IconButton size="small" onClick={() => setTokenModalOpen(false)}>
								<CloseIcon fontSize="small" />
							</IconButton>
						</div>
					</DialogTitle>
					<DialogContent>
						{newTokenValue ? (
							<>
								<Typography variant="body2" gutterBottom>
									{texts.settings.copyNow}
								</Typography>
								<div className={classes.tokenRevealBox}>{newTokenValue}</div>
							</>
						) : (
							<>
								<TextField
									className={classes.formField}
									label={texts.settings.tokenName}
									placeholder={texts.settings.tokenNamePlaceholder}
									variant="outlined"
									fullWidth
									value={tokenName}
									onChange={e => setTokenName(e.target.value)}
								/>
								<TextField
									className={classes.formField}
									select
									label={texts.settings.expirationTerm}
									placeholder={texts.settings.expirationPlaceholder}
									variant="outlined"
									fullWidth
									value={tokenExpiration}
									onChange={e => setTokenExpiration(e.target.value)}
									helperText={!tokenExpiration ? texts.settings.requiredField : ""}
									error={!tokenExpiration}
								>
									{localizedExpirationOptions.map(option => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</TextField>
								{/* O tipo so e escolhido na criacao: nao muda depois. */}
								{!editingToken ? (
									<TextField
										className={classes.formField}
										select
										label="Tipo do token"
										variant="outlined"
										fullWidth
										value={tokenScope}
										onChange={e => setTokenScope(e.target.value)}
										helperText={
											tokenScope === "webchat_links"
												? "Só abre e encerra o chat do app do assinante. É o que fica no servidor do provedor. Não muda depois de criado."
												: "Acesso completo à API da empresa."
										}
									>
										<MenuItem value="full">Completo (API)</MenuItem>
										<MenuItem value="webchat_links">Só chat do app do assinante</MenuItem>
									</TextField>
								) : null}
							</>
						)}
					</DialogContent>
					<DialogActions>
						<Button
							variant="outlined"
							color="primary"
							onClick={() => setTokenModalOpen(false)}
						>
							{newTokenValue ? texts.common.close : texts.common.cancel}
						</Button>
						{newTokenValue ? (
							<Tooltip title={texts.settings.copyToken}>
								<Button
									variant="contained"
									color="primary"
									startIcon={<FileCopyIcon />}
									onClick={handleCopyNewToken}
								>
									{texts.settings.copyToken}
								</Button>
							</Tooltip>
						) : (
							<Button
								variant="contained"
								color="primary"
								onClick={handleSaveToken}
								disabled={savingToken}
							>
								{savingToken ? texts.common.saving : editingToken ? texts.common.save : texts.settings.generateToken}
							</Button>
						)}
					</DialogActions>
				</Dialog>
			</Container>
		</div>
	);
};

export default Settings;
